import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, User } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  fetchPendingAvailability,
  approveAvailability,
  rejectAvailability,
  type PendingAvailability,
} from '@/lib/barristersApi';

const STATUS_LABEL: Record<string, string> = {
  available: 'Available',
  partial: 'Partial',
  busy: 'Busy',
  on_leave: 'On Leave',
  unavailable: 'Unavailable',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'outline'> = {
  available: 'success',
  partial: 'warning',
  busy: 'danger',
  on_leave: 'outline',
  unavailable: 'danger',
};

export default function AvailabilityApprovalsPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  /** Notes longer than two lines are opened by tapping them — see the note cell below. */
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  const toggleNote = (id: string) =>
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const loadPending = async () => {
    setLoading(true);
    try {
      const data = await fetchPendingAvailability();
      setPending(data);
    } catch (err) {
      console.error('Failed to fetch pending availability:', err);
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleApprove = async (id: string) => {
    setProcessing(id);
    try {
      await approveAvailability(id);
      toast.success('Availability approved');
      setPending((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Failed to approve:', err);
      toast.error('Failed to approve availability');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id: string) => {
    const note = prompt('Rejection reason (optional):');
    if (note === null) return;
    setProcessing(id);
    try {
      await rejectAvailability(id, note || undefined);
      toast.success('Availability rejected');
      setPending((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Failed to reject:', err);
      toast.error('Failed to reject availability');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Availability Approvals"
        subtitle="Review and approve barrister availability requests"
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-4 text-lg font-medium text-foreground">All caught up!</p>
            <p className="mt-1 text-sm text-muted-foreground">
              No pending availability requests to review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="border-b border-border bg-muted/30 px-5 py-2.5 text-xs text-muted-foreground">
            {pending.length} pending request{pending.length !== 1 ? 's' : ''}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-[0.06em] text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium sm:px-5">Barrister</th>
                  {/*
                    * Date and Status FOLD below md, the same way Bank accounts folds its payment
                    * details: with five columns the table was 652px wide inside a 353px card, so
                    * a clerk on a phone had to swipe the table sideways to reach the Approve and
                    * Reject buttons — the decision was visible and the decision was not. Nothing
                    * is dropped; both come back on the line under the barrister's name.
                    */}
                  <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Date</th>
                  <th className="hidden px-4 py-2 text-left font-medium md:table-cell">Status</th>
                  <th className="hidden px-4 py-2 text-left font-medium lg:table-cell">Submitted</th>
                  <th className="px-2 py-2 text-right font-medium sm:px-4">Actions</th>
                </tr>
              </thead>
              {/*
                * One <tbody> per request, not one for the whole table, because a request with a
                * note is TWO rows and the line between requests has to go round both of them.
                */}
              {pending.map((item) => (
                <tbody key={item.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <tr>
                    <td className="px-3 py-2 sm:px-5">
                      <div className="flex items-center gap-1.5 font-medium text-foreground">
                        <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        {item.barristerName}
                      </div>
                      {/* Folded, not dropped: the two columns that give way below md. */}
                      <div className="mt-1 flex flex-wrap items-center gap-2 pl-5 text-[12.5px] text-muted-foreground md:hidden">
                        {formatDate(item.date)}
                        <Badge variant={STATUS_VARIANT[item.status] ?? 'outline'}>
                          {STATUS_LABEL[item.status] ?? item.status}
                        </Badge>
                      </div>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-2 text-[12.5px] md:table-cell">{formatDate(item.date)}</td>
                    <td className="hidden px-4 py-2 md:table-cell">
                      <Badge variant={STATUS_VARIANT[item.status] ?? 'outline'}>
                        {STATUS_LABEL[item.status] ?? item.status}
                      </Badge>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-2 text-[12.5px] text-muted-foreground lg:table-cell">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(item.createdAt).toLocaleDateString('en-GB')}
                      </span>
                    </td>
                    <td className="px-2 py-2 sm:px-4">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2 sm:px-3"
                          onClick={() => handleReject(item.id)}
                          disabled={processing === item.id}
                        >
                          <XCircle className="mr-1 hidden h-4 w-4 sm:inline" />
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="px-2 sm:px-3"
                          onClick={() => handleApprove(item.id)}
                          disabled={processing === item.id}
                        >
                          <CheckCircle className="mr-1 hidden h-4 w-4 sm:inline" />
                          Approve
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {item.notes && (
                    /*
                     * THE NOTE IS THE REASON FOR THE REQUEST, and the clerk decides on it with
                     * the buttons at the end of the row. It used to be a `truncate`d line inside
                     * the barrister cell whose full text lived in a `title` tooltip — and there
                     * is no hover on a phone, so on the device this screen is most used on, a
                     * long note could not be read at all.
                     *
                     * It gets its own row across the table instead of width inside that cell:
                     * widening a column would push Approve and Reject further along a table that
                     * a phone already has to be swiped sideways to reach the end of (measured:
                     * 652px of table in a 353px card). A row that spans the columns, with the
                     * text capped at the width of the SCREEN, takes none of that width — it
                     * starts at the left edge, which is where the reader already is.
                     *
                     * Two lines by default, which is most notes whole; tap for the rest. The
                     * tooltip stays, as an extra for a mouse rather than the only way in.
                     */
                    <tr>
                      <td colSpan={5} className="px-3 pb-2 pt-0 sm:px-5">
                        <button
                          type="button"
                          onClick={() => toggleNote(item.id)}
                          aria-expanded={expandedNotes.has(item.id)}
                          title={item.notes}
                          /*
                           * `block` belongs ONLY in the open branch: `line-clamp-2` works by
                           * setting `display: -webkit-box`, and a `block` beside it wins on
                           * order and silently un-clamps the note — measured at 320px, that is
                           * a 24-line paragraph where two lines were meant.
                           */
                          className={`max-w-[calc(100vw-4rem)] text-left text-xs text-muted-foreground transition-colors hover:text-foreground ${
                            expandedNotes.has(item.id) ? 'block whitespace-pre-wrap break-words' : 'line-clamp-2'
                          }`}
                        >
                          {item.notes}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
